import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/config';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ArrowLeft, MessageSquare, Trash2, ChevronLeft, ChevronRight,
  ChevronDown, ChevronUp, Clock, User, Bot, ArrowUpRight, Filter
} from 'lucide-react';

export default function ChatHistoryPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const filterWidget = searchParams.get('widget') || '';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [widgets, setWidgets] = useState([]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [historyRes, widgetsRes] = await Promise.all([
        axios.get(`${API}/chat-history?page=${currentPage}&limit=10${filterWidget ? `&widget_id=${filterWidget}` : ''}`),
        axios.get(`${API}/widgets`),
      ]);
      setData(historyRes.data);
      setWidgets(widgetsRes.data);
    } catch {
      toast.error('Failed to load chat history');
    } finally {
      setLoading(false);
    }
  }, [currentPage, filterWidget]);

  useEffect(() => { loadData(); }, [loadData]);

  const goToPage = (page) => {
    const params = { page: page.toString() };
    if (filterWidget) params.widget = filterWidget;
    setSearchParams(params);
    window.scrollTo(0, 0);
  };

  const setFilter = (widgetId) => {
    const params = { page: '1' };
    if (widgetId) params.widget = widgetId;
    setSearchParams(params);
  };

  const deleteConversation = async (sessionId) => {
    if (!window.confirm('Delete this conversation?')) return;
    try {
      await axios.delete(`${API}/chat-history/${sessionId}`);
      toast.success('Conversation deleted');
      loadData();
    } catch { toast.error('Failed to delete'); }
  };

  const planLimit = data?.plan_limit || 10;
  const isUnlimited = planLimit >= 999999;

  // Build a map of widget_id -> bot_name + accent_color
  const widgetMap = {};
  widgets.forEach(w => { widgetMap[w.widget_id] = { name: w.bot_name, color: w.accent_color || '#10B981' }; });

  return (
    <div className="min-h-screen bg-background" data-testid="chat-history-page">
      <header className="border-b border-border px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/dashboard" className="text-muted-foreground hover:text-foreground transition-colors" data-testid="back-to-dashboard">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-sm font-semibold text-foreground">Chat History</h1>
            <p className="text-xs text-muted-foreground">
              {isUnlimited ? 'Unlimited conversations' : `${data?.total || 0} of ${planLimit} conversations`}
              {' · '}{user?.plan} plan
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Bot filter */}
        {widgets.length > 1 && (
          <div className="mb-6 flex items-center gap-2 flex-wrap" data-testid="widget-filter">
            <Filter className="h-4 w-4 text-muted-foreground shrink-0" />
            <button
              onClick={() => setFilter('')}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                !filterWidget ? 'bg-[#10B981] text-white' : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
              data-testid="filter-all"
            >
              All Bots
            </button>
            {widgets.map(w => (
              <button
                key={w.widget_id}
                onClick={() => setFilter(w.widget_id)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors flex items-center gap-1.5 ${
                  filterWidget === w.widget_id ? 'bg-[#10B981] text-white' : 'bg-muted text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`filter-${w.widget_id}`}
              >
                <span className="w-2 h-2 rounded-full shrink-0" style={{ background: w.accent_color || '#10B981' }} />
                {w.bot_name}
              </button>
            ))}
          </div>
        )}

        {/* Plan limit notice */}
        {!isUnlimited && data && (
          <div className="mb-6 p-3 rounded-lg bg-muted/50 border border-border flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              Your <strong className="text-foreground">{data.plan}</strong> plan stores up to <strong className="text-foreground">{planLimit}</strong> conversations. Oldest are auto-deleted when limit is reached.
            </p>
            <Link to="/pricing" className="text-xs text-[#10B981] hover:underline flex items-center gap-1 shrink-0 ml-4">
              Upgrade <ArrowUpRight className="h-3 w-3" />
            </Link>
          </div>
        )}

        {loading ? (
          <div className="text-center py-16 text-muted-foreground text-sm">Loading conversations...</div>
        ) : !data?.conversations?.length ? (
          <div className="text-center py-16 border border-dashed border-border rounded-xl">
            <MessageSquare className="h-10 w-10 text-muted-foreground mx-auto mb-3 opacity-40" />
            <p className="text-sm text-muted-foreground">
              {filterWidget ? 'No conversations for this bot yet.' : 'No chat conversations yet.'}
            </p>
            <p className="text-xs text-muted-foreground mt-1">Conversations will appear here when visitors chat with your bots.</p>
            {filterWidget && (
              <button onClick={() => setFilter('')} className="text-xs text-[#10B981] hover:underline mt-2">
                Show all bots
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="space-y-2" data-testid="conversations-list">
              {data.conversations.map((convo) => {
                const isExpanded = expandedId === convo.session_id;
                const msgCount = convo.messages?.length || 0;
                const firstUserMsg = convo.messages?.find(m => m.role === 'user')?.content || 'No messages';
                const lastActivity = convo.updated_at ? new Date(convo.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
                const botInfo = widgetMap[convo.widget_id] || { name: convo.bot_name || 'Bot', color: '#10B981' };

                return (
                  <div key={convo.session_id} className="rounded-xl border border-border bg-card overflow-hidden" data-testid={`convo-${convo.session_id}`}>
                    {/* Conversation header */}
                    <div
                      className="p-4 flex items-center justify-between cursor-pointer hover:bg-muted/30 transition-colors"
                      onClick={() => setExpandedId(isExpanded ? null : convo.session_id)}
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${botInfo.color}15` }}>
                          <MessageSquare className="h-4 w-4" style={{ color: botInfo.color }} />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">{firstUserMsg}</p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5 flex-wrap">
                            <span className="flex items-center gap-1">
                              <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: botInfo.color }} />
                              {botInfo.name}
                            </span>
                            <span>&middot;</span>
                            <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{lastActivity}</span>
                            <span>&middot;</span>
                            <span>{msgCount} messages</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-destructive hover:text-destructive"
                          onClick={(e) => { e.stopPropagation(); deleteConversation(convo.session_id); }}
                          data-testid={`delete-convo-${convo.session_id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                        {isExpanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                      </div>
                    </div>

                    {/* Expanded messages */}
                    {isExpanded && convo.messages && (
                      <div className="border-t border-border p-4 bg-muted/10 max-h-[400px] overflow-y-auto space-y-3">
                        {convo.messages.map((msg, i) => (
                          <div key={i} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                            {msg.role !== 'user' && (
                              <div className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ background: `${botInfo.color}15` }}>
                                <Bot className="h-3 w-3" style={{ color: botInfo.color }} />
                              </div>
                            )}
                            <div className={`max-w-[75%] px-3 py-2 rounded-xl text-xs leading-relaxed ${
                              msg.role === 'user'
                                ? 'bg-[#2563EB] text-white rounded-br-sm'
                                : 'bg-card border border-border text-foreground rounded-bl-sm'
                            }`}>
                              {msg.content}
                              {msg.ts && (
                                <p className={`text-[9px] mt-1 ${msg.role === 'user' ? 'text-white/50' : 'text-muted-foreground'}`}>
                                  {new Date(msg.ts).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                                </p>
                              )}
                            </div>
                            {msg.role === 'user' && (
                              <div className="w-6 h-6 rounded-full bg-[#2563EB]/10 flex items-center justify-center shrink-0 mt-0.5">
                                <User className="h-3 w-3 text-[#2563EB]" />
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {data.pages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-8" data-testid="history-pagination">
                <Button variant="outline" size="sm" disabled={currentPage <= 1} onClick={() => goToPage(currentPage - 1)} className="gap-1">
                  <ChevronLeft className="h-4 w-4" /> Prev
                </Button>
                {Array.from({ length: data.pages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === data.pages || Math.abs(p - currentPage) <= 2)
                  .reduce((acc, p, i, arr) => {
                    if (i > 0 && p - arr[i - 1] > 1) acc.push('...');
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, i) =>
                    p === '...' ? (
                      <span key={`e-${i}`} className="px-2 text-xs text-muted-foreground">...</span>
                    ) : (
                      <Button key={p} variant={p === currentPage ? 'default' : 'outline'} size="sm" className={`w-9 h-9 ${p === currentPage ? 'bg-[#10B981] hover:bg-[#059669] text-white' : ''}`} onClick={() => goToPage(p)}>
                        {p}
                      </Button>
                    )
                  )}
                <Button variant="outline" size="sm" disabled={currentPage >= data.pages} onClick={() => goToPage(currentPage + 1)} className="gap-1">
                  Next <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}

            <p className="text-center text-xs text-muted-foreground mt-4">
              Showing {((currentPage - 1) * 10) + 1}–{Math.min(currentPage * 10, data.total)} of {data.total} conversations
              {filterWidget && widgets.find(w => w.widget_id === filterWidget) && (
                <span> · Filtered by: <strong>{widgets.find(w => w.widget_id === filterWidget)?.bot_name}</strong></span>
              )}
            </p>
          </>
        )}
      </main>
    </div>
  );
}
